/**
 * A custom HTML element for managing facet filters in a product grid.
 * Handles dynamic updates to filters, product counts, and product grids.
 */
class FacetFiltersForm extends HTMLElement {
  /**
   * Initializes the FacetFiltersForm instance by setting up event listeners and debounced submission.
   */
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // Debounce the form submission to improve performance.
    this.debouncedOnSubmit = debounce((event) => {
      this.updateFacetAccordion(event.target);
      this.onSubmitHandler(event);
    }, 800);

    // Attach input event listener to the form with debounced submission.
    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    // Attach keyup event listener to close facets on "Escape" key press.
    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  // Find all details-accordion elements and reinitialize them
  static initDetailsAccordion() {
    document
      .querySelectorAll('details-accordion')
      .forEach((detailsAccordion) => {
        if (typeof detailsAccordion.init === 'function') {
          detailsAccordion.init();
        }
      });
  }

  /**
   * Sets up the popstate event listener to handle browser history navigation.
   */
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams =
        event.state && event.state.searchParams != null
          ? event.state.searchParams
          : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  /**
   * Toggles the active state of facet removal buttons.
   * @param {boolean} [disable=true] - Whether to disable the buttons.
   */
  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  /**
   * Renders the product grid, counts, and filters based on the provided search parameters.
   * Updates the URL hash if required.
   * @param {string} searchParams - The search parameters to use for rendering.
   * @param {Event} event - The triggering event (optional).
   * @param {boolean} [updateURLHash=true] - Whether to update the URL hash.
   */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById(
      'ProductCountDesktop'
    );

    // Show loading state
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document
      .getElementById('ProductGridContainer')
      .querySelector('.collection')
      .classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    // Fetch or render sections from the cache
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    FacetFiltersForm.initDetailsAccordion();
  }

  /**
   * Fetches the filter data from the server and updates the DOM.
   * @param {string} url - The URL to fetch data from.
   * @param {Event} event - The triggering event.
   */
  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        if (typeof initializeScrollAnimationTrigger === 'function')
          initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  /**
   * Renders the cached filter data and updates the DOM.
   * @param {Function} filterDataUrl - Function to match the cached data.
   * @param {Event} event - The triggering event.
   */
  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function')
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  /**
   * Updates the product grid container content based on fetched HTML.
   * @param {string} html - The fetched HTML.
   */
  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  /**
   * Updates the product count containers based on fetched HTML.
   * @param {string} html - The fetched HTML.
   */
  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  /**
   * Updates the filters in the DOM based on fetched HTML.
   * Handles removal of outdated filters and rendering of updated filters.
   * @param {string} html - The fetched HTML.
   * @param {Event} event - The triggering event.
   */
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (
        !Array.from(facetDetailsElementsFromFetch).some(
          ({ id }) => currentElement.id === id
        )
      ) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (element) => !matchesId(element)
    );
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
      matchesId
    );

    // Add or update filters in the DOM
    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document?.getElementById(elementToRender.id);
      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document
            ?.querySelector(`#${elementToRender.parentElement.id} .js-filter`)
            ?.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event?.target?.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest('.js-filter')
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID)
        );

        const newFacetDetailsElement =
          document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains(
          'js-mobile-facets-details'
        )
          ? `.js-mobile-facets-close-button`
          : `.js-facets-summary`;
        const newElementToActivate =
          newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    FacetFiltersForm.initDetailsAccordion();
  }

  /**
   * Renders active facets for both mobile and desktop.
   * Updates the innerHTML of active facet containers with new content from the provided HTML.
   * Toggles the active state of facet removal buttons.
   * @param {Document} html - The fetched HTML document containing updated facets.
   */
  static renderActiveFacets(html) {
    // Selectors for active facets in mobile and desktop views.
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
    ];

    // Iterate over selectors to update active facet elements.
    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return; // Skip if no active facets element is found in the new HTML.
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML; // Update the DOM with new content.
    });

    // Enable facet removal buttons after updating.
    FacetFiltersForm.toggleActiveFacets(false);
  }

  /**
   * Updates additional elements such as mobile facets count and sorting options.
   * Ensures new content replaces existing content only when the structure matches.
   * @param {Document} html - The fetched HTML document containing updated elements.
   */
  static renderAdditionalElements(html) {
    // Selectors for elements to be updated in the mobile view.
    const mobileElementSelectors = [
      '.js-mobile-facets-open',
      '.js-mobile-facets-count',
      '.sorting',
    ];

    // Iterate over selectors to update matching elements.
    mobileElementSelectors.forEach((selector) => {
      const newElements = html.querySelectorAll(selector); // Get updated elements from the new HTML.
      const existingElements = document.querySelectorAll(selector); // Get current elements from the DOM.

      // Ensure matching element counts before updating.
      if (newElements.length !== existingElements.length) return;

      // Update existing elements with new content.
      existingElements.forEach((existingElement, index) => {
        const newElement = newElements[index];
        if (newElement) {
          existingElement.innerHTML = newElement.innerHTML;
        }
      });
    });
  }

  /**
   * Updates counts for specific facet elements based on new source content.
   * Handles "Show More" functionality to display hidden items when expanded.
   * @param {Element} source - The source element containing updated facet counts.
   * @param {Element} target - The target element to be updated.
   */
  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.js-facets-summary');
    const sourceSummary = source.querySelector('.js-facets-summary');

    // Update facet summary content.
    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.js-facets-header');
    const sourceHeaderElement = source.querySelector('.js-facets-header');

    // Update facet header content.
    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.js-facets-wrap');
    const sourceWrapElement = source.querySelector('.js-facets-wrap');

    // Update facets wrapper content and handle hidden items for "Show More" functionality.
    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector('show-more-button .label-show-more.hidden')
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets-item.hidden')
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace('hidden', 'show-more-item')
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  /**
   * Updates counts for mobile facets based on new source content.
   * Replaces the entire mobile facets list content.
   * @param {Element} source - The source element containing updated mobile facets.
   * @param {Element} target - The target element to be updated.
   */
  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.js-mobile-facets-list');
    const sourceFacetsList = source.querySelector('.js-mobile-facets-list');

    // Update the mobile facets list content.
    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  /**
   * Updates the URL hash with the given search parameters.
   * Pushes the updated state into the browser history.
   * @param {string} searchParams - The search parameters to include in the URL hash.
   */
  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '', // Title is empty as it's not required here.
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}` // Construct the new URL.
    );
  }

  /**
   * Retrieves the section IDs for the product grid.
   * This is used to fetch the appropriate sections from the server.
   * @returns {Array<Object>} - An array of section objects containing section IDs.
   */
  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id, // Retrieves the section ID from the product grid element.
      },
    ];
  }

  /**
   * Creates a URL search parameter string from the form data.
   * @param {HTMLFormElement} form - The form element to extract data from.
   * @returns {string} - The URL-encoded string of form data.
   */
  createSearchParams(form) {
    const formData = new FormData(form); // Create a FormData object from the provided form.
    return new URLSearchParams(formData).toString(); // Convert form data into a URL-encoded string.
  }

  onCloseSortDetails() {
    const detailsId = this.getAttribute('sort-details-id');

    if (detailsId?.length === 0) {
      return null;
    }

    const details = document?.getElementById(detailsId);

    if (!details) {
      return null;
    }

    if (details.hasAttribute('open')) {
      details.removeAttribute('open');
    }
  }

  /**
   * Submits the provided search parameters and triggers page rendering.
   * @param {string} searchParams - The search parameters to include in the request.
   * @param {Event} event - The event object triggering this action.
   */
  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event); // Render the page with updated search parameters.
  }

  /**
   * Handles form submission for the facet filters.
   * Distinguishes between mobile and desktop form submissions.
   * Combines search parameters from relevant forms before submission.
   * @param {Event} event - The form submission event.
   */
  onSubmitHandler(event) {
    event.preventDefault(); // Prevent default form submission behavior.

    // Select all forms within the `facet-filters-form` component.
    const sortFilterForms = document.querySelectorAll(
      'facet-filters-form form'
    );
    // Handle submission for mobile facet checkbox clicks.
    if (event.srcElement.className === 'js-mobile-facets-checkbox') {
      const searchParams = this.createSearchParams(
        event.target.closest('form') // Find the closest form to the event target.
      );
      this.onSubmitForm(searchParams, event); // Submit the search parameters.
    } else {
      const forms = [];
      const isMobile =
        event.target.closest('form').id === 'FacetFiltersFormMobile'; // Check if the form is the mobile-specific form.

      // Loop through all forms and create search parameters based on the form type.
      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          // For desktop forms, process only the relevant forms.
          if (
            form.id === 'FacetSortForm' ||
            form.id === 'FacetFiltersForm' ||
            form.id === 'FacetSortDrawerForm'
          ) {
            forms.push(this.createSearchParams(form));
            this.onCloseSortDetails();
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          // For mobile, process the mobile-specific form.
          forms.push(this.createSearchParams(form));
        }
      });

      // Join all collected search parameters and submit them.
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  /**
   * Handles clicks on active filters to remove them.
   * Prevents default link behavior and triggers a page update.
   * @param {Event} event - The click event triggered on the active filter link.
   */
  onActiveFilterClick(event) {
    event.preventDefault(); // Prevent the default behavior of the link click.

    // Toggle the active state of facets (e.g., hide/show active facets).
    FacetFiltersForm.toggleActiveFacets();

    // Extract the URL query string from the clicked link.
    const url =
      event.currentTarget.href.indexOf('?') === -1
        ? '' // If no query string exists, return an empty string.
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf('?') + 1 // Extract the portion of the URL after the '?'.
          );

    // Render the page with the updated URL query string.
    FacetFiltersForm.renderPage(url);
  }

  /**
   * Updates the accordion label to reflect the currently selected filter value.
   * If the target input has the `data-update-facet-label` attribute, appends the
   * selected option's text to the base label (e.g. "Sort: Best-selling").
   * @param {HTMLElement} target - The input element that triggered the change.
   */
  updateFacetAccordion(target) {
    if (!target) return;

    const labelSpan = this.querySelector('[data-facet-label]');
    if (!labelSpan) return;

    const baseLabel = (labelSpan.getAttribute('data-facet-label') || '').trim();

    if (target.hasAttribute('data-update-facet-label')) {
      const labelText = target.closest('label')?.textContent.trim() || target.value;
      const separator = baseLabel && labelText ? ' ' : '';
      labelSpan.textContent = `${baseLabel}${separator}${labelText}`;
    }
  };
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// Define a custom element for handling price range inputs
class PriceRange extends HTMLElement {
  constructor() {
    super();

    // Add event listeners to each input field for handling changes and keydown events
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    // Initialize the min and max values for the inputs
    this.setMinAndMaxValues();
  }

  // Event handler for when the input value changes
  onRangeChange(event) {
    // Ensure the input value is within the allowed range
    this.adjustToValidValues(event.currentTarget);

    // Update the min and max values of the inputs dynamically
    this.setMinAndMaxValues();
  }

  // Event handler for keydown events to restrict input to valid characters
  onKeyDown(event) {
    // Allow meta keys (e.g., Command on macOS)
    if (event.metaKey) return;

    // Define a pattern of allowed keys
    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;

    // Prevent default behavior if the key does not match the allowed pattern
    if (!event.key.match(pattern)) event.preventDefault();
  }

  // Method to set the dynamic min and max values for the input fields
  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0]; // First input for minimum value
    const maxInput = inputs[1]; // Second input for maximum value

    // Set the max attribute of the min input based on the max input's value
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);

    // Set the min attribute of the max input based on the min input's value
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);

    // If no min value is provided, default the min for the max input to 0
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);

    // If no max value is provided, reset the max for the min input to its original max
    if (maxInput.value === '')
      minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  // Method to validate and adjust the input values to be within the allowed range
  adjustToValidValues(input) {
    const value = Number(input.value); // Get the current input value
    const min = Number(input.getAttribute('data-min')); // Minimum allowed value
    const max = Number(input.getAttribute('data-max')); // Maximum allowed value

    // Adjust the input value if it's below the minimum
    if (value < min) input.value = min;

    // Adjust the input value if it's above the maximum
    if (value > max) input.value = max;
  }
}

// Register the custom element for price range inputs
customElements.define('price-range', PriceRange);

// Define a custom element for handling the removal of facet filters
class FacetRemove extends HTMLElement {
  constructor() {
    super();

    // Get the link element within the facet remove component
    const facetLink = this.querySelector('a');

    // Set the role attribute for accessibility purposes
    facetLink.setAttribute('role', 'button');

    // Add click and keyup event listeners for handling filter removal
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();

      // Trigger the filter removal on the Space key
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  // Method to handle closing/removing a filter
  closeFilter(event) {
    event.preventDefault();

    // Get the closest facet-filters-form or find it in the document
    const form =
      this.closest('facet-filters-form') ||
      document.querySelector('facet-filters-form');

    // Trigger the onActiveFilterClick method of the form to handle filter removal
    form.onActiveFilterClick(event);
  }
}

// Register the custom element for facet filter removal
customElements.define('facet-remove', FacetRemove);
class DetailsAccordion extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.toggleDetails.bind(this);
  }

  init() {
    this.summary = this.querySelector('.mobile-facets-summary');
    this.content = this.querySelector('.mobile-facets-submenu');
    this.arrow = this.summary?.querySelector('.mobile-facets-arrow');

    if (!this.summary || !this.content || !this.arrow) return;
    this.summary.addEventListener('click', this.handleClick);
    this.setInitialState();
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.summary?.removeEventListener('click', this.handleClick);
  }

  setInitialState() {
    this.updateState(this.classList.contains('open'));
  }

  updateState(isOpen) {
    this.content.style.maxHeight = isOpen
      ? `${this.content.scrollHeight}px`
      : '0';
    this.setAttribute('aria-expanded', isOpen);
    this.arrow.classList.toggle('rotate-180', isOpen);
    this.arrow.classList.toggle('rotate-0', !isOpen);
  }

  toggleDetails(event) {
    event.preventDefault();
    document.querySelectorAll('details-accordion').forEach((el) => {
      if (el !== this) {
        el.classList.remove('open');
        el.updateState(false);
      }
    });

    const isOpen = this.classList.toggle('open');
    this.updateState(isOpen);
  }
}

customElements.define('details-accordion', DetailsAccordion);
